interface DuneParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  drag: number;
  opacity: number;
}

export class DunesBackground {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width = window.innerWidth;
  private height = window.innerHeight;
  private particles: DuneParticle[] = [];
  private maxParticles = 140;
  private time = 0;
  private animId: number | null = null;
  private lastTime = Date.now();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get 2D canvas context');
    }
    this.ctx = context;
    
    this.resize();
    window.addEventListener('resize', () => this.resize());
    
    // Populate initial particles across the screen
    for (let i = 0; i < this.maxParticles; i++) {
      const p: DuneParticle = { x: 0, y: 0, vx: 0, vy: 0, size: 0, drag: 0, opacity: 0 };
      this.spawnParticle(p, false);
      p.x = Math.random() * this.width; // spread initially
      this.particles.push(p);
    }
  }

  private resize() {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    
    // Scale for high DPI displays
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = this.width * dpr;
    this.canvas.height = this.height * dpr;
    this.ctx.scale(dpr, dpr);
    
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;
  }

  private getDuneHeight(layer: number, x: number, time: number): number {
    // Phase speeds represent shifting dunes over long cycles
    const speeds = [0.015, 0.035, 0.065];
    const phase = time * speeds[layer];
    
    if (layer === 0) {
      // Background dune: slow rolling high peaks
      return this.height * 0.52 + Math.sin(x * 0.0012 + phase) * 80 + Math.cos(x * 0.0025 - phase * 0.4) * 25;
    } else if (layer === 1) {
      // Midground dune: intermediate ridges
      return this.height * 0.65 + Math.sin(x * 0.0022 - phase * 1.1) * 55 + Math.sin(x * 0.0008 + phase) * 35;
    } else {
      // Foreground dune: sharp peaks
      return this.height * 0.78 + Math.sin(x * 0.0035 + phase * 0.9) * 32 + Math.cos(x * 0.0018 - phase) * 18;
    }
  }

  private spawnParticle(p: DuneParticle, onLeftEdge: boolean) {
    p.size = 1.0 + Math.random() * 1.8;
    p.drag = 0.02 + Math.random() * 0.05;
    p.opacity = 0.15 + Math.random() * 0.45;
    
    if (onLeftEdge) {
      p.x = -10;
      p.y = Math.random() * this.height * 0.85;
    } else {
      p.x = Math.random() * this.width;
      p.y = Math.random() * this.height * 0.85;
    }
    
    // Initial velocity
    p.vx = 40 + Math.random() * 60;
    p.vy = -5 + Math.random() * 10;
  }

  public start() {
    this.lastTime = Date.now();
    const frame = () => {
      const now = Date.now();
      let dt = (now - this.lastTime) / 1000;
      this.lastTime = now;
      
      // Cap dt to prevent particle escape during tab switches
      if (dt > 0.1) dt = 0.1;
      
      this.time += dt;
      this.tick(dt);
      this.animId = requestAnimationFrame(frame);
    };
    this.animId = requestAnimationFrame(frame);
  }

  public stop() {
    if (this.animId !== null) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  }

  private tick(dt: number) {
    this.ctx.clearRect(0, 0, this.width, this.height);
    
    // 1. Draw Dunes (Back, Mid, Front)
    for (let layer = 0; layer < 3; layer++) {
      this.ctx.beginPath();
      
      const startY = this.getDuneHeight(layer, 0, this.time);
      this.ctx.moveTo(0, startY);
      
      // Plot curve across canvas
      const step = 8;
      for (let x = step; x <= this.width + step; x += step) {
        const y = this.getDuneHeight(layer, x, this.time);
        this.ctx.lineTo(x, y);
      }
      
      this.ctx.lineTo(this.width, this.height);
      this.ctx.lineTo(0, this.height);
      this.ctx.closePath();
      
      // Create rich desert canyon gradient (shadow highlights from top-left sun)
      const grad = this.ctx.createLinearGradient(0, this.height * 0.45, this.width, this.height);
      if (layer === 0) {
        grad.addColorStop(0, '#1f130c'); // background: deep shadows
        grad.addColorStop(1, '#0c0705');
      } else if (layer === 1) {
        grad.addColorStop(0, '#321f15'); // midground: warm canyon wood
        grad.addColorStop(1, '#110a08');
      } else {
        grad.addColorStop(0, '#482d1f'); // foreground: bright terracotta clay
        grad.addColorStop(1, '#1b110c');
      }
      
      this.ctx.fillStyle = grad;
      this.ctx.fill();
    }
    
    // 2. Physics & Draw Particles
    // Modulate wind over time (simulating gusts)
    const windX = 110 + Math.sin(this.time * 0.3) * 55 + Math.cos(this.time * 0.7) * 20;
    const windY = -6 + Math.sin(this.time * 0.5) * 6;
    const gravity = 25.0; // gravity force
    
    this.ctx.fillStyle = 'url(#sand-grad)'; // Fallback sand gradient or sandy amber color
    const particleColor = '#dfb76c';
    
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      
      // Calculate wind resistance acceleration: F = drag * (wind - velocity)
      const ax = p.drag * (windX - p.vx);
      const ay = p.drag * (windY - p.vy) + gravity;
      
      p.vx += ax * dt;
      p.vy += ay * dt;
      
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      
      // Check collision with the foreground dune (the top boundary we walk on)
      const duneY = this.getDuneHeight(2, p.x, this.time);
      
      if (p.y >= duneY) {
        p.y = duneY; // snap to surface
        
        // Calculate dune normal and tangent slopes
        const yPlus = this.getDuneHeight(2, p.x + 2, this.time);
        const yMinus = this.getDuneHeight(2, p.x - 2, this.time);
        const dy = (yPlus - yMinus) / 4; // dy/dx
        
        // Tangent vector components
        const len = Math.sqrt(1 + dy * dy);
        const tx = 1 / len;
        const ty = dy / len;
        
        // Normal vector components
        const nx = -ty;
        const ny = tx;
        
        // Decompose velocity
        const vTangent = p.vx * tx + p.vy * ty;
        let vNormal = p.vx * nx + p.vy * ny;
        
        // Bounce / Restitution check
        if (vNormal < 0) {
          vNormal = -vNormal * 0.18; // bounce out of ground slightly
        }
        
        // Reconstruct with friction
        p.vx = vTangent * tx * 0.82 + vNormal * nx;
        p.vy = vTangent * ty * 0.82 + vNormal * ny;
        
        // Settling check: sand gathers in valleys or dies out
        if (Math.random() < 0.05 || p.vx < 8.0) {
          this.spawnParticle(p, true);
          continue;
        }
      }
      
      // Respawn if blown off-screen
      if (p.x > this.width + 10 || p.y > this.height + 10 || p.x < -20) {
        this.spawnParticle(p, true);
        continue;
      }
      
      // Render particle
      this.ctx.fillStyle = particleColor;
      this.ctx.globalAlpha = p.opacity;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    this.ctx.globalAlpha = 1.0; // restore alpha
  }
}

// Global initialization helper
export function initDunes(): DunesBackground | null {
  const canvas = document.getElementById('dunes-canvas') as HTMLCanvasElement | null;
  if (!canvas) return null;
  
  const db = new DunesBackground(canvas);
  db.start();
  return db;
}
